import * as THREE from "three";
import { CLOUD_GLSL, cloudShadowAt, setCloudShadow, type CloudShadowUniforms } from "./cloud-shadow";
import { setGrade } from "./grade";
import type { DayLook } from "./daylight";
import type { LowPolyLook, LowPolyUniforms } from "./lowpoly";
import type { ShaftPass } from "./shaft-pass";

// The time of day on the low-poly look (decision 0034's sky, haze, shafts and palette per time; 0048's day-night
// cycle): a sky dome with a sun disc, a moon, stars and a cloud layer, all from uniforms; light shaft cards (one
// instanced, additive draw, under the haze); and `applyDayLook`, which writes a frame's `DayLook` into the look's
// lights, haze, lamps, wetness and exposure, the dome, the cards and the high tier's shaft pass. Nothing here adds or
// removes a light or changes a define after it is built, so a whole day and every weather draw with the programs
// compiled at boot (a warm-up still has to draw each piece once, visible).

/** The dome's uniforms (the gradient's names, so `LowPolyLook.setLook` still writes it, plus the night and clouds). */
export interface SkyDomeUniforms {
  top: THREE.IUniform<THREE.Color>;
  horizon: THREE.IUniform<THREE.Color>;
  below: THREE.IUniform<THREE.Color>;
  /** Toward the sun's true place (the disc and its glow). */
  sunDir: THREE.IUniform<THREE.Vector3>;
  sunColor: THREE.IUniform<THREE.Color>;
  /** 0 with the sun under the horizon, 1 above. */
  uSunUp: THREE.IUniform<number>;
  uMoonDir: THREE.IUniform<THREE.Vector3>;
  uStars: THREE.IUniform<number>;
  uCloud: THREE.IUniform<number>;
  uCloudLit: THREE.IUniform<THREE.Color>;
  uCloudShade: THREE.IUniform<THREE.Color>;
  uTime: THREE.IUniform<number>;
}

const DOME_FRAG = /* glsl */ `
  uniform vec3 top; uniform vec3 horizon; uniform vec3 below; uniform vec3 sunDir; uniform vec3 sunColor;
  uniform float uSunUp; uniform vec3 uMoonDir; uniform float uStars; uniform float uCloud; uniform vec3 uCloudLit;
  uniform vec3 uCloudShade; uniform float uTime;
  varying vec3 vDir;
  float h31(vec3 p) { p = fract(p * 0.1031); p += dot(p, p.zyx + 31.32); return fract((p.x + p.y) * p.z); }
  float h21(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  float vn(vec2 p) {
    vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  float fbm(vec2 p) { float a = 0.5; float s = 0.0; for (int i = 0; i < 4; i++) { s += a * vn(p); p = p * 2.03 + vec2(17.1, 9.2); a *= 0.5; } return s; }
  void main() {
    vec3 d = normalize(vDir);
    vec3 c = d.y >= 0.0 ? mix(horizon, top, smoothstep(0.0, 0.35, d.y)) : mix(horizon, below, smoothstep(0.0, -0.25, d.y));
    float up = smoothstep(-0.02, 0.06, d.y);
    // the cloud layer: a plane overhead, drifting; one tap toward the sun for a lit side, a little posterised
    float dens = 0.0; vec3 cloud = uCloudShade;
    if (uCloud > 0.001 && d.y > 0.0) {
      vec2 uv = d.xz / (d.y + 0.12) * 1.3 + uTime * vec2(0.010, 0.004);
      float n = fbm(uv);
      float n2 = fbm(uv + sunDir.xz * 0.12);
      dens = smoothstep(0.66 - 0.4 * uCloud, 0.86 - 0.3 * uCloud, n) * smoothstep(0.0, 0.18, d.y) * min(1.0, uCloud * 1.6);
      float lit = clamp(0.62 + (n - n2) * 5.0, 0.0, 1.0);
      lit = mix(lit, floor(lit * 3.0 + 0.5) / 3.0, 0.25);
      cloud = mix(uCloudShade, uCloudLit, lit);
    }
    // stars: a hashed cell grid, twinkling, hidden by cloud
    vec3 q = d * 170.0; vec3 cell = floor(q); float hs = h31(cell);
    float star = step(0.986, hs) * smoothstep(0.34, 0.0, length(fract(q) - 0.5)) * (0.65 + 0.35 * sin(uTime * 2.1 + hs * 61.0));
    c += vec3(0.85, 0.9, 1.0) * star * uStars * up * (1.0 - dens) * 1.8;
    // the moon: a disc with a soft halo, brighter as the night deepens
    float mm = dot(d, uMoonDir);
    c += vec3(0.86, 0.9, 1.0) * (smoothstep(0.99935, 0.99958, mm) * 1.5 + pow(max(mm, 0.0), 40.0) * 0.1) * uStars * up * (1.0 - 0.85 * dens);
    // the sun: a wide glow and an HDR disc (bloom on the tiers that have it)
    float sm = max(dot(d, sunDir), 0.0);
    c += sunColor * (pow(sm, 48.0) * 0.6 + pow(sm, 6.0) * 0.07) * uSunUp;
    c += sunColor * smoothstep(0.99955, 0.99975, sm) * 5.0 * uSunUp * up * (1.0 - dens);
    c = mix(c, cloud, dens);
    gl_FragColor = vec4(c, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }`;

/** A sky dome with the night and a cloud layer (for `createLowPolyLook`'s `sky` option). Uniforms only: one program
 *  for every hour and weather. */
export function createSkyDome(o: { radius: number }): THREE.Mesh {
  const uniforms: SkyDomeUniforms = {
    top: { value: new THREE.Color() },
    horizon: { value: new THREE.Color() },
    below: { value: new THREE.Color() },
    sunDir: { value: new THREE.Vector3(0, 1, 0) },
    sunColor: { value: new THREE.Color() },
    uSunUp: { value: 1 },
    uMoonDir: { value: new THREE.Vector3(0, -1, 0) },
    uStars: { value: 0 },
    uCloud: { value: 0 },
    uCloudLit: { value: new THREE.Color(1, 1, 1) },
    uCloudShade: { value: new THREE.Color(0.6, 0.65, 0.75) },
    uTime: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    name: "lowpoly-sky-dome",
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
    vertexShader: /* glsl */ `varying vec3 vDir; void main() { vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_Position.z = gl_Position.w; }`,
    fragmentShader: DOME_FRAG,
  });
  const sky = new THREE.Mesh(new THREE.SphereGeometry(o.radius, 32, 16), mat);
  sky.name = "lowpoly-sky";
  sky.frustumCulled = false;
  // drawn last of the opaque world at the far plane (depth test on, no depth write), so the clouds and stars shade only
  // the pixels the world left empty (the Wildbrush rule; drawn first it shaded every pixel); transparent things after
  sky.renderOrder = 1e4;
  return sky;
}

/** The dome's uniforms. */
export const skyDomeUniforms = (dome: THREE.Mesh): SkyDomeUniforms => (dome.material as THREE.ShaderMaterial).uniforms as unknown as SkyDomeUniforms;

/** One light shaft: a ground point, the beam's length toward the sun and its width (m). */
export interface ShaftSpot {
  x: number;
  y: number;
  z: number;
  length: number;
  width: number;
}

/** Light shaft cards: one instanced draw. */
export interface Shafts {
  readonly mesh: THREE.Mesh;
  readonly count: number;
  /** Write a frame's look (strength, colour, the sun's direction); hidden at strength 0. */
  apply(d: DayLook, time: number): void;
  /** The strength last applied (0 to 1, the key's and the weather's). */
  strength(): number;
  /** Whether the beams respect cloud shadow (`createShafts`' `clouds`). */
  readonly clouds: boolean;
}

const SHAFT_VERT = /* glsl */ `
  attribute vec4 aSpot; attribute vec2 aSize;
  uniform vec3 uSunDir;
  varying vec2 vUv; varying float vSeed; varying vec3 vWorld;
  void main() {
    // the beam rises from its ground point toward the sun (never flatter than about 20 degrees), turned about that
    // axis to face the camera
    vec3 axis = normalize(vec3(uSunDir.x, max(uSunDir.y, 0.35), uSunDir.z));
    vec3 base = aSpot.xyz;
    vec3 side = normalize(cross(axis, cameraPosition - base));
    float w = aSize.x * (0.7 + 0.6 * uv.y);
    vec3 p = base + axis * (uv.y * aSpot.w) + side * ((uv.x - 0.5) * w);
    vWorld = p; vUv = uv; vSeed = aSize.y;
    gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
  }`;

const SHAFT_FRAG = /* glsl */ `
  uniform vec3 uColor; uniform float uStrength; uniform float uTime;
  uniform vec4 uLpHaze; uniform vec2 uLpHaze2;
  varying vec2 vUv; varying float vSeed; varying vec3 vWorld;
  void main() {
    float across = sin(3.14159265 * vUv.x); across *= across;
    // the base clamped: under MSAA a pixel the card's tip only partly covers shades at its centre, off the card, where
    // vUv.y passes 1; pow of a negative base is NaN on D3D (ANGLE), and bloom's blur spread it over the frame
    float along = smoothstep(0.0, 0.12, vUv.y) * pow(max(1.0 - vUv.y, 0.0), 1.3);
    float sway = 0.75 + 0.25 * sin(uTime * 0.7 + vSeed * 6.2832 + vUv.x * 3.0);
    float d = length(vWorld - cameraPosition);
    float nearFade = smoothstep(1.5, 7.0, d);
    // under the haze: the same factor the surface applies at this distance and height
    float fd = 1.0 - exp(-max(0.0, d - uLpHaze.x) * uLpHaze.y);
    float fh = exp(-max(0.0, vWorld.y - uLpHaze.z) * uLpHaze.w);
    float haze = clamp(fd * mix(1.0, fh, uLpHaze2.x), 0.0, uLpHaze2.y);
    float a = across * along * sway * nearFade * (1.0 - haze) * uStrength;
    // no tone mapping or sRGB encoding here: an additive term encoded on its own (the tiers that draw straight to the
    // canvas) would be boosted several times over (sRGB lifts small values) and outshine the same beam on a tier whose
    // post chain encodes the sum once. Unencoded, both tiers add about the same light.
    gl_FragColor = vec4(uColor * a, 1.0);
  }`;

/** The shaft cards' shaders when they respect cloud shadow (IMP-0025): the vertex shader finds the shade at the beam's
 *  ground point with the surface's own field (the same uniforms, shared), and the fragment shader takes that share of
 *  the beam away, as the surface takes it from the key light. Without the option the cards keep the shaders above,
 *  text for text. */
const SHAFT_CLOUD_VERT = SHAFT_VERT.replace("uniform vec3 uSunDir;", `uniform vec3 uSunDir;\n${CLOUD_GLSL.decl}varying float vShade;`).replace(
  "vWorld = p; vUv = uv; vSeed = aSize.y;",
  `vWorld = p; vUv = uv; vSeed = aSize.y;
    vShade = 0.0;
    if (uLpCloud.x > 0.0) {
      float lpC = lpCloudF(aSpot.xz * uLpCloud.w + uLpCloudAt);
      vShade = uLpCloud.x * (1.0 - smoothstep(uLpCloud.y - uLpCloud.z, uLpCloud.y + uLpCloud.z, lpC));
    }`,
);
const SHAFT_CLOUD_FRAG = SHAFT_FRAG.replace("varying vec2 vUv; varying float vSeed; varying vec3 vWorld;", "varying vec2 vUv; varying float vSeed; varying vec3 vWorld; varying float vShade;").replace(
  "* (1.0 - haze) * uStrength;",
  "* (1.0 - haze) * uStrength * (1.0 - vShade);",
);

/** Planted defect for the shafts' cloud check: `ignoreClouds` builds the cards without the cloud field even when asked
 *  (a beam stands in a cloud's shade). */
export const SHAFT_FAULTS = { ignoreClouds: false };

/** Light shaft cards at `spots`, fading under the look's haze (its uniforms are shared by reference). `gain` scales
 *  the beam's brightness (the game's value). `clouds` (the look's uniforms; default none): a beam whose ground point
 *  lies in cloud shadow loses the shade's share of its light, as the ground there loses the key's (IMP-0025). */
export function createShafts(o: { spots: readonly ShaftSpot[]; haze: LowPolyUniforms; gain?: number; clouds?: CloudShadowUniforms }): Shafts {
  const plane = new THREE.PlaneGeometry(1, 1, 1, 4);
  plane.translate(0.5, 0.5, 0); // uv and position both 0..1, so the vertex shader places it
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = plane.index;
  geo.setAttribute("position", plane.getAttribute("position"));
  geo.setAttribute("uv", plane.getAttribute("uv"));
  const spot = new Float32Array(Math.max(1, o.spots.length) * 4);
  const size = new Float32Array(Math.max(1, o.spots.length) * 2);
  o.spots.forEach((s, i) => {
    spot.set([s.x, s.y, s.z, s.length], i * 4);
    size.set([s.width, (i * 0.618034) % 1], i * 2);
  });
  geo.setAttribute("aSpot", new THREE.InstancedBufferAttribute(spot, 4));
  geo.setAttribute("aSize", new THREE.InstancedBufferAttribute(size, 2));
  geo.instanceCount = o.spots.length;
  const gain = o.gain ?? 1;
  const clouds = SHAFT_FAULTS.ignoreClouds ? undefined : o.clouds;
  const mat = new THREE.ShaderMaterial({
    name: "lowpoly-shafts",
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    fog: false,
    uniforms: {
      uSunDir: { value: new THREE.Vector3(0, 1, 0) },
      uColor: { value: new THREE.Color() },
      uStrength: { value: 0 },
      uTime: { value: 0 },
      uLpHaze: o.haze.uLpHaze,
      uLpHaze2: o.haze.uLpHaze2,
      ...(clouds ? { uLpCloud: clouds.uLpCloud, uLpCloudAt: clouds.uLpCloudAt } : {}),
    },
    vertexShader: clouds ? SHAFT_CLOUD_VERT : SHAFT_VERT,
    fragmentShader: clouds ? SHAFT_CLOUD_FRAG : SHAFT_FRAG,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = "lowpoly-shafts";
  mesh.frustumCulled = false;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // after the opaque world, before other transparent things (rain)
  mesh.renderOrder = 10;
  let last = 0;
  return {
    mesh,
    count: o.spots.length,
    apply(d, time) {
      const u = mat.uniforms;
      // only the sun makes shafts; they fade as it nears the horizon
      const up = Math.min(1, Math.max(0, (d.sun.elevation + 0.02) / 0.12));
      last = d.shafts * up;
      u.uStrength!.value = last * gain;
      u.uSunDir!.value.set(d.sun.dir[0], d.sun.dir[1], d.sun.dir[2]);
      u.uColor!.value.setRGB(d.key.colour[0], d.key.colour[1], d.key.colour[2], THREE.SRGBColorSpace);
      u.uTime!.value = time;
      mesh.visible = last > 0.001 && o.spots.length > 0;
    },
    strength: () => last,
    clouds: !!clouds,
  };
}

/** The share of a beam's light the cloud shadow leaves at world (x, z), 0 to 1: the shaft cards' and the pass's sum
 *  on the CPU (1 with no cloud shadow). */
export function shaftLight(d: Pick<DayLook, "clouds">, x: number, z: number): number {
  return 1 - cloudShadowAt(d.clouds.shadow, x, z);
}

const eye = new THREE.Vector3();

/** Write a frame's `DayLook` into the look (lights, haze, glow, lamps, wetness, cloud shadow, grade, exposure) and, when given, the dome,
 *  the shaft cards and the shaft pass. `time` (s) drives the clouds, stars and shafts' slow motion. */
export function applyDayLook(o: { look: LowPolyLook; renderer: THREE.WebGLRenderer; camera: THREE.Camera; dome?: THREE.Mesh | null; shafts?: Shafts | null; pass?: ShaftPass | null }, d: DayLook, time: number): void {
  const { look } = o;
  const srgb = THREE.SRGBColorSpace;
  look.sun.color.setRGB(d.key.colour[0], d.key.colour[1], d.key.colour[2], srgb);
  look.sun.intensity = d.key.intensity;
  look.sunDirection.set(d.key.dir[0], d.key.dir[1], d.key.dir[2]);
  look.fill.color.setRGB(d.fill.sky[0], d.fill.sky[1], d.fill.sky[2], srgb);
  look.fill.groundColor.setRGB(d.fill.ground[0], d.fill.ground[1], d.fill.ground[2], srgb);
  look.fill.intensity = d.fill.intensity;
  const u = look.uniforms;
  u.uLpHazeColor.value.setRGB(d.haze.colour[0], d.haze.colour[1], d.haze.colour[2], srgb);
  u.uLpHaze.value.set(d.haze.near, d.haze.density, d.haze.base, d.haze.falloff);
  u.uLpHaze2.value.set(d.haze.height, d.haze.max);
  u.uLpGlow.value = d.glow;
  u.uLpLamp.value = d.lamps;
  u.uLpWet.value = d.wetDark;
  setCloudShadow(u, d.clouds.shadow);
  setGrade(u, d.grade);
  o.renderer.toneMappingExposure = d.exposure;
  if (o.dome) {
    const s = skyDomeUniforms(o.dome);
    s.top.value.setRGB(d.sky.top[0], d.sky.top[1], d.sky.top[2], srgb);
    s.horizon.value.setRGB(d.sky.horizon[0], d.sky.horizon[1], d.sky.horizon[2], srgb);
    s.below.value.setRGB(d.sky.ground[0], d.sky.ground[1], d.sky.ground[2], srgb);
    s.sunDir.value.set(d.sun.dir[0], d.sun.dir[1], d.sun.dir[2]);
    s.sunColor.value.setRGB(d.sun.colour[0], d.sun.colour[1], d.sun.colour[2], srgb);
    s.uSunUp.value = Math.min(1, Math.max(0, (d.sun.elevation + 0.1) / 0.12));
    s.uMoonDir.value.set(d.moon.dir[0], d.moon.dir[1], d.moon.dir[2]);
    s.uStars.value = d.stars;
    s.uCloud.value = d.clouds.cover;
    s.uCloudLit.value.setRGB(d.clouds.lit[0], d.clouds.lit[1], d.clouds.lit[2], srgb);
    s.uCloudShade.value.setRGB(d.clouds.shade[0], d.clouds.shade[1], d.clouds.shade[2], srgb);
    s.uTime.value = time;
  }
  o.shafts?.apply(d, time);
  if (o.pass) {
    const up = Math.min(1, Math.max(0, (d.sun.elevation + 0.02) / 0.12));
    // a pass that respects cloud shadow fades by the shade where the eye stands (the cloud between it and the sun)
    let light = 1;
    if (o.pass.clouds && d.clouds.shadow.strength > 0) {
      o.camera.getWorldPosition(eye);
      light = shaftLight(d, eye.x, eye.z);
    }
    o.pass.setSun(o.camera, d.sun.dir, d.shafts * up * light, d.key.colour);
  }
}
